/**
 * Historique des passages — KPI, filtres, tableau (GET /api/history-passages).
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import {
  AlertTriangle,
  Camera,
  CheckCircle2,
  Clock,
  Eye,
  FileSpreadsheet,
  Filter,
  ImageIcon,
  Loader2,
  MapPin,
  RefreshCw,
  Shield,
} from "lucide-react";
import { api } from "../../../lib/axios";
import type { PassageApiItem, TourneeApiItem } from "../planificateur/planningTypes";
import {
  clientLabel,
  STATUTS_PASSAGE_FILTRE,
} from "../planificateur/planningDashboardUtils";
import {
  gpsBadgeKind,
  passageValidationColumnLabel,
  photoFraction,
  validationQualiteSubtitle,
  validationUi,
} from "../../terrain-supervision/chef/passageExecutionUtils";
import { PassageExecutionDrawer } from "../../terrain-supervision/chef/PassageExecutionDrawer";

function localIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function validationBadgeClasses(v: ReturnType<typeof validationUi>): string {
  if (v === "valide") return "bg-emerald-500/15 text-emerald-300 ring-emerald-500/30";
  if (v === "rejete") return "bg-rose-500/15 text-rose-200 ring-rose-500/25";
  if (v === "clos_anomalies") return "bg-amber-500/15 text-amber-200 ring-amber-500/35";
  if (v === "en_attente") return "bg-amber-500/15 text-amber-200 ring-amber-500/25";
  return "bg-slate-700/50 text-slate-400 ring-slate-600/40";
}

function gpsBadgeText(kind: ReturnType<typeof gpsBadgeKind>): string {
  if (kind === "ok") return "OK";
  if (kind === "suspect") return "Suspect";
  return "Non relevé";
}

function gpsBadgeClasses(kind: ReturnType<typeof gpsBadgeKind>): string {
  if (kind === "ok") return "bg-emerald-500/15 text-emerald-300 ring-emerald-500/30";
  if (kind === "suspect") return "bg-rose-500/15 text-rose-200 ring-rose-500/30";
  return "bg-slate-700/50 text-slate-400 ring-slate-600/40";
}

type HistoryKpisResponse = {
  passagesFiltrés: number;
  passagesExecutes: number;
  passagesValides: number;
  passagesEnValidation: number;
  tauxValidationPct: number;
  tauxConformiteGpsPct: number;
  tauxPhotosCompletesPct: number;
  tauxAnomaliesPct: number;
  tempsMoyenExecutionMin: number | null;
  tauxFacturationPct: number;
  tauxRejetSuperviseurPct: number;
};

function gpsConformiteAccent(pct: number): "emerald" | "amber" | "rose" {
  if (pct >= 90) return "emerald";
  if (pct >= 70) return "amber";
  return "rose";
}

export default function HistoriquePassagesPage(): React.JSX.Element {
  const today = useMemo(() => new Date(), []);
  const defaultFin = localIsoDate(today);
  const defaultDebut = localIsoDate(new Date(today.getFullYear(), today.getMonth(), today.getDate() - 30));

  const [dateDebut, setDateDebut] = useState(defaultDebut);
  const [dateFin, setDateFin] = useState(defaultFin);
  const [clientId, setClientId] = useState("");
  const [zoneId, setZoneId] = useState("");
  const [tourneeId, setTourneeId] = useState("");
  const [equipeId, setEquipeId] = useState("");
  const [statut, setStatut] = useState("");
  const [facturableOnly, setFacturableOnly] = useState(false);
  const [anomaliesOnly, setAnomaliesOnly] = useState(false);

  const [items, setItems] = useState<PassageApiItem[]>([]);
  const [serverKpis, setServerKpis] = useState<HistoryKpisResponse | null>(null);
  const [tournees, setTournees] = useState<TourneeApiItem[]>([]);
  const [zones, setZones] = useState<{ id: number; nom?: string }[]>([]);
  const [loading, setLoading] = useState(true);

  const [drawerPassage, setDrawerPassage] = useState<PassageApiItem | null>(null);
  const [drawerSection, setDrawerSection] = useState<"gps" | "photos" | null>(null);

  const tourneeById = useMemo(() => {
    const m = new Map<number, TourneeApiItem>();
    for (const t of tournees) m.set(t.id, t);
    return m;
  }, [tournees]);

  const loadRefs = useCallback(async () => {
    try {
      const [zRes, tRes] = await Promise.all([
        api.get("/zones").catch(() => ({ data: [] })),
        api
          .get<{ items?: TourneeApiItem[] }>("/tournees", { params: { limit: 100, page: 1 } })
          .catch(() => ({ data: { items: [] } })),
      ]);
      const zRaw = zRes.data as unknown;
      const zArr = Array.isArray(zRaw) ? zRaw : (zRaw as { data?: unknown })?.data;
      setZones(Array.isArray(zArr) ? (zArr as { id: number; nom?: string }[]) : []);
      setTournees(Array.isArray(tRes.data?.items) ? tRes.data.items : []);
    } catch {
      setZones([]);
      setTournees([]);
    }
  }, []);

  const loadPassages = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = {
        limit: "200",
      };
      if (dateDebut.trim()) params.dateDebut = dateDebut.trim();
      if (dateFin.trim()) params.dateFin = dateFin.trim();
      if (clientId.trim()) params.clientId = clientId.trim();
      if (zoneId.trim()) params.zoneId = zoneId.trim();
      if (tourneeId.trim()) params.tourneeId = tourneeId.trim();
      if (equipeId.trim()) params.equipeId = equipeId.trim();
      if (statut.trim()) params.statut = statut.trim();
      if (facturableOnly) params.facturableOnly = "true";
      if (anomaliesOnly) params.anomaliesOnly = "true";

      const res = await api.get<{
        items?: PassageApiItem[];
        kpis?: HistoryKpisResponse;
      }>("/history-passages", { params });
      setItems(Array.isArray(res.data?.items) ? res.data.items : []);
      setServerKpis(res.data?.kpis ?? null);
    } catch {
      toast.error("Impossible de charger l’historique des passages.");
      setItems([]);
      setServerKpis(null);
    } finally {
      setLoading(false);
    }
  }, [
    anomaliesOnly,
    clientId,
    dateDebut,
    dateFin,
    equipeId,
    facturableOnly,
    statut,
    tourneeId,
    zoneId,
  ]);

  useEffect(() => {
    void loadRefs();
  }, [loadRefs]);

  useEffect(() => {
    void loadPassages();
  }, [loadPassages]);

  useEffect(() => {
    const onExternal = (): void => {
      void loadRefs();
      void loadPassages();
    };
    window.addEventListener("adminExecutionPassagesRefresh", onExternal);
    return () => window.removeEventListener("adminExecutionPassagesRefresh", onExternal);
  }, [loadPassages, loadRefs]);

  const equipeOptions = useMemo(() => {
    const m = new Map<number, string>();
    for (const t of tournees) {
      if (t.equipe?.id && t.equipe.nom) m.set(t.equipe.id, t.equipe.nom);
    }
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [tournees]);

  /** Tournée issue du payload historique (équipe incluse) quand `/tournees` du jour ne couvre pas les dates passées. */
  const tourneeFromPassageEmbedded = (p: PassageApiItem): TourneeApiItem | null => {
    const tn = p.tournee;
    if (!tn?.id) return null;
    return {
      id: tn.id,
      numero: tn.numero ?? null,
      date: tn.date,
      statut: tn.statut,
      equipe: tn.equipe ?? undefined,
    };
  };

  const pickTournee = (p: PassageApiItem): TourneeApiItem | null => {
    if (p.tourneeId != null) {
      const fromMap = tourneeById.get(p.tourneeId);
      if (fromMap) return fromMap;
    }
    return tourneeFromPassageEmbedded(p);
  };

  const exportExcel = (): void => {
    if (!items.length) {
      toast.warning("Aucune ligne à exporter.");
      return;
    }
    const rows = items.map((p) => ({
      Client: clientLabel(p),
      Passage: p.id,
      DatePrevue: p.datePrevue,
      DateExecution: p.dateRealisee ? String(p.dateRealisee) : "",
      DureeMin: typeof p.dureeReelleMin === "number" ? p.dureeReelleMin : "",
      Equipe: pickTournee(p)?.equipe?.nom ?? "",
      Validation: passageValidationColumnLabel(p),
      GPS: gpsBadgeText(gpsBadgeKind(p)),
      Photos: photoFraction(p).label,
      Facturable: p.billingLabel ?? (p.isFacturable ? "Oui" : "Non"),
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Historique");
    XLSX.writeFile(wb, `historique-passages-${localIsoDate(new Date())}.xlsx`);
    toast.success("Export Excel généré.");
  };

  const sk = serverKpis;
  const gpsPct = sk?.tauxConformiteGpsPct ?? 0;
  const gpsRing =
    gpsConformiteAccent(gpsPct) === "emerald"
      ? "ring-emerald-500/25"
      : gpsConformiteAccent(gpsPct) === "amber"
        ? "ring-amber-500/30"
        : "ring-rose-500/30";

  return (
    <div className="min-h-screen bg-slate-950 px-4 py-6 text-slate-100 md:px-8">
      <header className="mb-6 flex flex-col gap-4 border-b border-slate-800 pb-6 md:flex-row md:items-start md:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-white md:text-3xl">
            Centre d’audit — historique terrain
          </h1>
          <p className="mt-2 max-w-3xl text-sm text-slate-400">
            Passages exécutés, en validation, validés, clôturés anomalies ou rejetés après contrôle. Les créneaux
            uniquement planifiés sans trace terrain ne sont pas listés ici.
          </p>
          <p className="mt-2 text-xs text-slate-500">
            Source unique : GET /api/history-passages — les KPI sont calculés sur le même jeu de lignes que le tableau
            (filtres appliqués côté serveur).
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => void loadPassages()}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-600 bg-slate-800 px-4 py-2 text-sm hover:bg-slate-800/90"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            Actualiser
          </button>
          <button
            type="button"
            onClick={exportExcel}
            className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-500"
          >
            <FileSpreadsheet className="h-4 w-4" /> Export Excel
          </button>
        </div>
      </header>

      <section className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 2xl:grid-cols-6">
        <article className="rounded-2xl border border-slate-800 bg-slate-900/70 p-4 shadow-md ring-1 ring-slate-500/25">
          <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-slate-500">
            <Filter className="h-3.5 w-3.5 text-slate-400" />
            Passages (filtre)
          </div>
          <p className="mt-2 text-2xl font-bold tabular-nums text-white">{sk?.passagesFiltrés ?? items.length}</p>
          <p className="mt-1 text-xs text-slate-500">Lignes tableau · même périmètre que KPI</p>
        </article>
        <article className="rounded-2xl border border-slate-800 bg-slate-900/70 p-4 shadow-md ring-1 ring-sky-500/20">
          <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-slate-500">
            <Clock className="h-3.5 w-3.5 text-sky-400" />
            Exécutés terrain
          </div>
          <p className="mt-2 text-2xl font-bold tabular-nums text-white">{sk?.passagesExecutes ?? "—"}</p>
          <p className="mt-1 text-xs text-slate-500">Démarrés / réalisés / en cours de validation</p>
        </article>
        <article className="rounded-2xl border border-slate-800 bg-slate-900/70 p-4 shadow-md ring-1 ring-emerald-500/20">
          <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-slate-500">
            <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />
            Validés superviseur
          </div>
          <p className="mt-2 text-2xl font-bold tabular-nums text-white">{sk?.passagesValides ?? "—"}</p>
          <p className="mt-1 text-xs text-slate-500">Clôture favorable ou avec anomalies maîtrisées</p>
        </article>
        <article className="rounded-2xl border border-slate-800 bg-slate-900/70 p-4 shadow-md ring-1 ring-amber-500/25">
          <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-slate-500">
            <Shield className="h-3.5 w-3.5 text-amber-300" />
            En validation
          </div>
          <p className="mt-2 text-2xl font-bold tabular-nums text-white">{sk?.passagesEnValidation ?? "—"}</p>
          <p className="mt-1 text-xs text-slate-500">En attente décision superviseur</p>
        </article>
        <article className="rounded-2xl border border-slate-800 bg-slate-900/70 p-4 shadow-md ring-1 ring-sky-500/20">
          <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-slate-500">
            <CheckCircle2 className="h-3.5 w-3.5 text-sky-300" />
            Taux validation
          </div>
          <p className="mt-2 text-2xl font-bold tabular-nums text-white">{sk ? `${sk.tauxValidationPct}%` : "—"}</p>
          <p className="mt-1 text-xs text-slate-500">Validés / exécutés</p>
        </article>
        <article className={`rounded-2xl border border-slate-800 bg-slate-900/70 p-4 shadow-md ring-1 ${gpsRing}`}>
          <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-slate-500">
            <MapPin className="h-3.5 w-3.5 text-emerald-300" />
            Conformité GPS
          </div>
          <p className="mt-2 text-2xl font-bold tabular-nums text-white">{sk ? `${sk.tauxConformiteGpsPct}%` : "—"}</p>
          <p className="mt-1 text-xs text-slate-500">Parmi passages avec relevé GPS · non suspect</p>
        </article>
        <article className="rounded-2xl border border-slate-800 bg-slate-900/70 p-4 shadow-md ring-1 ring-violet-500/20">
          <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-slate-500">
            <Camera className="h-3.5 w-3.5 text-violet-300" />
            Photos complètes
          </div>
          <p className="mt-2 text-2xl font-bold tabular-nums text-white">{sk ? `${sk.tauxPhotosCompletesPct}%` : "—"}</p>
          <p className="mt-1 text-xs text-slate-500">Avant + après</p>
        </article>
        <article className="rounded-2xl border border-slate-800 bg-slate-900/70 p-4 shadow-md ring-1 ring-rose-500/20">
          <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-slate-500">
            <AlertTriangle className="h-3.5 w-3.5 text-rose-300" />
            Taux anomalies
          </div>
          <p className="mt-2 text-2xl font-bold tabular-nums text-white">{sk ? `${sk.tauxAnomaliesPct}%` : "—"}</p>
          <p className="mt-1 text-xs text-slate-500">Suspects ou clôture anomalies</p>
        </article>
        <article className="rounded-2xl border border-slate-800 bg-slate-900/70 p-4 shadow-md ring-1 ring-violet-500/20">
          <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-slate-500">
            <Shield className="h-3.5 w-3.5 text-violet-300" />
            Taux facturation
          </div>
          <p className="mt-2 text-2xl font-bold tabular-nums text-white">{sk ? `${sk.tauxFacturationPct}%` : "—"}</p>
          <p className="mt-1 text-xs text-slate-500">Marqués facturables</p>
        </article>
        <article className="rounded-2xl border border-slate-800 bg-slate-900/70 p-4 shadow-md ring-1 ring-rose-500/20">
          <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-slate-500">
            <AlertTriangle className="h-3.5 w-3.5 text-rose-200" />
            Rejet superviseur
          </div>
          <p className="mt-2 text-2xl font-bold tabular-nums text-white">{sk ? `${sk.tauxRejetSuperviseurPct}%` : "—"}</p>
          <p className="mt-1 text-xs text-slate-500">Non effectué validé</p>
        </article>
        <article className="rounded-2xl border border-slate-800 bg-slate-900/70 p-4 shadow-md ring-1 ring-slate-500/25">
          <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-slate-500">
            <Clock className="h-3.5 w-3.5 text-slate-400" />
            Temps moyen exécution
          </div>
          <p className="mt-2 text-2xl font-bold tabular-nums text-white">
            {sk?.tempsMoyenExecutionMin != null ? `${sk.tempsMoyenExecutionMin} min` : "—"}
          </p>
          <p className="mt-1 text-xs text-slate-500">Moyenne sur lignes filtrées</p>
        </article>
      </section>

      <section className="mb-6 rounded-2xl border border-slate-800 bg-slate-900/40 p-4">
        <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Filtres</p>
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6">
          <label className="text-xs text-slate-400">
            Date début
            <input
              type="date"
              value={dateDebut}
              onChange={(e) => setDateDebut(e.target.value)}
              className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-2 py-1.5 text-sm text-white"
            />
          </label>
          <label className="text-xs text-slate-400">
            Date fin
            <input
              type="date"
              value={dateFin}
              onChange={(e) => setDateFin(e.target.value)}
              className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-2 py-1.5 text-sm text-white"
            />
          </label>
          <label className="text-xs text-slate-400">
            Client (id)
            <input
              type="number"
              min={1}
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              placeholder="ex. 12"
              className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-2 py-1.5 text-sm text-white"
            />
          </label>
          <label className="text-xs text-slate-400">
            Zone
            <select
              value={zoneId}
              onChange={(e) => setZoneId(e.target.value)}
              className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-2 py-1.5 text-sm text-white"
            >
              <option value="">Toutes</option>
              {zones.map((z) => (
                <option key={z.id} value={z.id}>
                  {z.nom ?? `Zone #${z.id}`}
                </option>
              ))}
            </select>
          </label>
          <label className="text-xs text-slate-400">
            Équipe
            <select
              value={equipeId}
              onChange={(e) => setEquipeId(e.target.value)}
              className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-2 py-1.5 text-sm text-white"
            >
              <option value="">Toutes</option>
              {equipeOptions.map(([id, nom]) => (
                <option key={id} value={id}>
                  {nom}
                </option>
              ))}
            </select>
          </label>
          <label className="text-xs text-slate-400">
            Tournée (id)
            <input
              type="number"
              min={1}
              value={tourneeId}
              onChange={(e) => setTourneeId(e.target.value)}
              placeholder="ex. 5"
              className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-2 py-1.5 text-sm text-white"
            />
          </label>
          <label className="text-xs text-slate-400">
            Statut
            <select
              value={statut}
              onChange={(e) => setStatut(e.target.value)}
              className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-2 py-1.5 text-sm text-white"
            >
              {STATUTS_PASSAGE_FILTRE.map((s) => (
                <option key={s || "all"} value={s}>
                  {s || "Tous"}
                </option>
              ))}
            </select>
          </label>
          <label className="flex cursor-pointer items-center gap-2 pt-6 text-xs text-slate-300">
            <input
              type="checkbox"
              checked={facturableOnly}
              onChange={(e) => setFacturableOnly(e.target.checked)}
              className="rounded border-slate-600"
            />
            Facturable uniquement
          </label>
          <label className="flex cursor-pointer items-center gap-2 pt-6 text-xs text-slate-300">
            <input
              type="checkbox"
              checked={anomaliesOnly}
              onChange={(e) => setAnomaliesOnly(e.target.checked)}
              className="rounded border-slate-600"
            />
            Anomalies GPS uniquement
          </label>
        </div>
      </section>

      <section className="overflow-x-auto rounded-2xl border border-slate-800 bg-slate-900/30">
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-20 text-slate-400">
            <Loader2 className="h-6 w-6 animate-spin" /> Chargement…
          </div>
        ) : (
          <table className="min-w-[960px] w-full border-collapse text-left text-sm">
            <thead>
              <tr className="border-b border-slate-800 bg-slate-900/80 text-[11px] font-bold uppercase tracking-wider text-slate-500">
                <th className="px-3 py-3">Client</th>
                <th className="px-3 py-3">Passage</th>
                <th className="px-3 py-3">Date prévue</th>
                <th className="px-3 py-3">Date exécution</th>
                <th className="px-3 py-3">Durée réelle</th>
                <th className="px-3 py-3">Équipe</th>
                <th className="px-3 py-3">Validation</th>
                <th className="px-3 py-3">GPS</th>
                <th className="px-3 py-3">Photos</th>
                <th className="px-3 py-3">Facturable</th>
                <th className="px-3 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => {
                const tn = pickTournee(p);
                const vu = validationUi(p);
                const validationSub = validationQualiteSubtitle(p);
                const gps = gpsBadgeKind(p);
                const ph = photoFraction(p);
                const dr = p.dateRealisee;
                return (
                  <tr key={p.id} className="border-b border-slate-800/80 hover:bg-slate-800/40">
                    <td className="px-3 py-2 font-medium">{clientLabel(p)}</td>
                    <td className="px-3 py-2 font-mono text-xs">#{p.id}</td>
                    <td className="px-3 py-2 text-xs text-slate-300">
                      {new Date(p.datePrevue).toLocaleString("fr-FR")}
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-300">
                      {dr ? new Date(String(dr)).toLocaleString("fr-FR") : "—"}
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-300">
                      {typeof p.dureeReelleMin === "number" && p.dureeReelleMin > 0 ? `${p.dureeReelleMin} min` : "—"}
                    </td>
                    <td className="px-3 py-2 text-xs">{tn?.equipe?.nom ?? "—"}</td>
                    <td className="px-3 py-2">
                      <div className="flex max-w-[12rem] flex-col gap-0.5">
                        <span className={`inline-flex w-fit rounded-full px-2 py-0.5 text-[11px] ring-1 ${validationBadgeClasses(vu)}`}>
                          {passageValidationColumnLabel(p)}
                        </span>
                        {validationSub ? (
                          <span className="text-[10px] leading-snug text-slate-500">{validationSub}</span>
                        ) : null}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] ring-1 ${gpsBadgeClasses(gps)}`}>
                        {gpsBadgeText(gps)}
                      </span>
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">{ph.label}</td>
                    <td className="px-3 py-2 text-xs text-slate-300">{p.billingLabel ?? (p.isFacturable ? "Oui" : "Non")}</td>
                    <td className="px-3 py-2 text-right">
                      <div className="flex flex-wrap justify-end gap-1">
                        <button
                          type="button"
                          onClick={() => {
                            setDrawerSection(null);
                            setDrawerPassage(p);
                          }}
                          className="inline-flex items-center gap-1 rounded-lg border border-slate-600 px-2 py-1 text-[11px] text-slate-200 hover:bg-slate-800"
                        >
                          <Eye className="h-3 w-3" /> Détail
                        </button>
                        <Link
                          to={`/admin/carte-terrain?passageId=${p.id}`}
                          className="inline-flex items-center gap-1 rounded-lg border border-sky-700/50 px-2 py-1 text-[11px] text-sky-200 hover:bg-sky-950/40"
                        >
                          <MapPin className="h-3 w-3" /> Carte
                        </Link>
                        <button
                          type="button"
                          onClick={() => {
                            setDrawerPassage(p);
                            setDrawerSection("photos");
                          }}
                          className="inline-flex items-center gap-1 rounded-lg border border-slate-600 px-2 py-1 text-[11px] text-slate-200 hover:bg-slate-800"
                        >
                          <ImageIcon className="h-3 w-3" /> Photos
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        {!loading && items.length === 0 ? (
          <p className="py-12 text-center text-slate-500">Aucun passage pour ces critères.</p>
        ) : null}
      </section>

      <PassageExecutionDrawer
        open={drawerPassage != null}
        passage={drawerPassage}
        tournee={drawerPassage ? pickTournee(drawerPassage) : null}
        initialSection={drawerSection}
        onClose={() => {
          setDrawerPassage(null);
          setDrawerSection(null);
        }}
      />

      <p className="mt-8 text-center text-xs text-slate-600">
        Export Excel : tableau ci-dessus ·{" "}
        <Link className="text-sky-500 hover:underline" to="/admin/carte-terrain">
          Carte terrain
        </Link>
      </p>
    </div>
  );
}
